import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAdminCourse,
  getAdminCourses,
  saveAdminCourseAllLanguages,
} from "../../../api/coursesAdmin";
import { useNotification } from "../../../context/NotificationContext";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { DashboardReloadButton } from "./dashboardUi";
import {
  btnAccent,
  btnPrimary,
  btnSecondary,
  langTabActive,
  langTabIdle,
} from "./adminProductUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

const emptyLangFields = () => ({
  title: "",
  description: "",
  gainHeading: "What you will gain:",
  gainItems: [{ text: "" }],
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  courseKey: "",
  isActive: true,
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

const mapRowToLangFields = (course) => ({
  title: course?.title || "",
  description: course?.description || "",
  gainHeading: course?.gainHeading || "What you will gain:",
  gainItems: Array.isArray(course?.gainItems) && course.gainItems.length
    ? course.gainItems.map((x) => ({ text: x?.text || "" }))
    : [{ text: "" }],
});

const mapCoursesToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  return {
    courseKey: primary?.courseKey || "",
    isActive: list.length ? list.every((r) => r?.isActive !== false) : true,
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

const toLanguageContent = (lang, fields) => ({
  language: lang,
  title: String(fields.title || "").trim(),
  description: String(fields.description || "").trim(),
  gainHeading: String(fields.gainHeading || "").trim() || "What you will gain:",
  gainItems: (fields.gainItems || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
});

const buildSavePayload = (form) => ({
  courseKey: String(form.courseKey || "").trim(),
  isActive: Boolean(form.isActive),
  // Title alone is enough for a language (AR/KU can be title-only).
  languages: LANGS.filter((lang) => (form.translations?.[lang]?.title || "").trim()).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const apiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "";
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.title && !data.errors) return data.title;
  if (data.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .flatMap(([key, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${key}: ${m}`))
      .join(" · ");
  }
  return error?.message || "";
};

const CoursesManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [editingCourseKey, setEditingCourseKey] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [saving, setSaving] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminCourses();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load courses",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const groupedCourses = useMemo(() => {
    const groups = new Map();
    items.forEach((course) => {
      const key = normKey(course?.courseKey) || `#${course?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(course);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normKey(rep?.courseKey) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [items]);

  const setLangField = (setForm, lang, partial) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], ...partial },
      },
    }));
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = Boolean((form.translations?.[lang]?.title || "").trim());
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  const renderLangFields = (form, setForm, activeLang) => {
    const fields = form.translations[activeLang] || emptyLangFields();
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm text-slate-700 sm:col-span-2">
          Title ({LANG_LABELS[activeLang]})
          <input
            type="text"
            value={fields.title}
            onChange={(e) => setLangField(setForm, activeLang, { title: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Description
          <textarea
            value={fields.description}
            onChange={(e) => setLangField(setForm, activeLang, { description: e.target.value })}
            className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Gain Heading
          <input
            type="text"
            value={fields.gainHeading}
            onChange={(e) => setLangField(setForm, activeLang, { gainHeading: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Gain Items</p>
          {fields.gainItems.map((row, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <input
                type="text"
                value={row?.text || ""}
                onChange={(e) =>
                  setForm((p) => {
                    const current = [...(p.translations[activeLang].gainItems || [])];
                    current[idx] = { text: e.target.value };
                    return {
                      ...p,
                      translations: {
                        ...p.translations,
                        [activeLang]: { ...p.translations[activeLang], gainItems: current },
                      },
                    };
                  })
                }
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
              <button
                type="button"
                onClick={() =>
                  setForm((p) => {
                    const current = (p.translations[activeLang].gainItems || []).filter(
                      (_, i) => i !== idx
                    );
                    return {
                      ...p,
                      translations: {
                        ...p.translations,
                        [activeLang]: {
                          ...p.translations[activeLang],
                          gainItems: current.length ? current : [{ text: "" }],
                        },
                      },
                    };
                  })
                }
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setForm((p) => ({
                ...p,
                translations: {
                  ...p.translations,
                  [activeLang]: {
                    ...p.translations[activeLang],
                    gainItems: [...(p.translations[activeLang].gainItems || []), { text: "" }],
                  },
                },
              }))
            }
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add gain item
          </button>
        </div>
      </div>
    );
  };

  const handleCreate = async () => {
    const anyTitle = LANGS.some((lang) => (createForm.translations[lang].title || "").trim());
    if (!createForm.courseKey.trim() || !anyTitle) {
      notify({ type: "error", title: "Course Key and at least one Title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      await saveAdminCourseAllLanguages(payload);
      notify({ type: "success", title: "Course saved for all languages" });
      setCreateForm(EMPTY_FORM);
      setActiveCreateLang("en");
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Save failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    setEditingCourseKey(group.key);
    setActiveEditLang("en");
    setEditForm(mapCoursesToForm(group.rows));
  };

  const closeEdit = () => {
    setEditingCourseKey(null);
    setEditForm(EMPTY_FORM);
    setActiveEditLang("en");
  };

  const saveEdit = async () => {
    if (!editingCourseKey) return;
    const anyTitle = LANGS.some((lang) => (editForm.translations[lang].title || "").trim());
    if (!editForm.courseKey.trim() || !anyTitle) {
      notify({ type: "error", title: "Course Key and at least one Title are required" });
      return;
    }
    setSaving(true);
    try {
      await saveAdminCourseAllLanguages(buildSavePayload(editForm));
      notify({ type: "success", title: "Course updated for all languages" });
      closeEdit();
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    const id = group?.rep?.id;
    if (!id) return;
    if (!window.confirm(`Delete course "${group.rep?.title || group.rep?.courseKey}" in all languages?`)) {
      return;
    }
    try {
      await deleteAdminCourse({ id, allLanguages: true });
      notify({ type: "success", title: "Course deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new course"
        description="Fill in every language (English, Arabic, Kurdish). Each language is saved together under one Course Key."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save course (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Course Key (shared across languages)
              <input
                type="text"
                value={createForm.courseKey}
                onChange={(e) => setCreateForm((p) => ({ ...p, courseKey: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                placeholder="e.g. drywall-basics"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isActive)}
                onChange={(e) => setCreateForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this course from the public site (all languages).
                </span>
              </span>
            </label>
          </div>

          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangFields(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your courses</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedCourses.length} item{groupedCourses.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading courses...
          </p>
        ) : groupedCourses.length ? (
          <div className="grid gap-3">
            {groupedCourses.map((group) => {
              const isActive = group.rows.every((r) => r?.isActive !== false);
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={group.rep?.title || group.rep?.courseKey || `Course #${group.rep?.id}`}
                  meta={[
                    group.rep?.courseKey ? `Key: ${group.rep.courseKey}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={isActive}
                  isEditing={editingCourseKey === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No courses found. Use &quot;Add new course&quot; above to create one.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingCourseKey)}
        title={`Edit course: ${editForm.courseKey || editingCourseKey || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public academy courses."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Course Key (shared across languages)
              <input
                type="text"
                value={editForm.courseKey}
                onChange={(e) => setEditForm((p) => ({ ...p, courseKey: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:mt-6">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isActive)}
                onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this course from the public site (all languages).
                </span>
              </span>
            </label>
          </div>

          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangFields(editForm, setEditForm, activeEditLang)}
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default CoursesManager;
